import React from 'react';
import { DateUtils, Misc, NeoModel, NumberUtils } from '@singularsystems/neo-core';
import CalculationGroup from '../../../Models/Portfolio/Calculation/CalculationGroup';
import { observer } from 'mobx-react';
import CalculationTranche from '../../../Models/Portfolio/Calculation/CalculationTranche';
import { Neo, NeoGrid, Views } from '@singularsystems/neo-react';
import AwardCostsLookup from '../../../Models/Portfolio/AwardCostsLookup';
import { AppService, Types } from '../../../TransactionsTypes';
import AwardCostsCriteria from 'st-common/Transactions/Models/AwardCostsCriteria';

interface IBalancesSummaryProps {
    group: CalculationGroup;
    hasCostsAlreadyPaidGroups: boolean;
    applyPayoutRatio: boolean;
}

interface ITrancheDetails {
    trancheId: number,
    vestingDate: Date,
    isVested: boolean,
    hasAcceleration: boolean,
    availableBalance: number,
    payoutRatio: number,
    hasUnprocessedAwardCondition: boolean,
    currentValueConverted: number,
    remainingAwardDebtConverted: number,
    profitLossConverted: number,
    releaseDate: Date,
    hasBeenReleased: boolean,
}

@NeoModel
class BalancesTrancheSummaryVM extends Views.ViewModelBase {

    public constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private portfolioApiClient = AppService.get(Types.Transactions.ApiClients.PortfolioApiClient),
    ) {
        super(taskRunner);
    }

    public awardCostsLookup: AwardCostsLookup = new AwardCostsLookup();

    public showAwardCostsModal: boolean = false;

    public showVestingSchedule: boolean = false;

    public hasLinkedRates: boolean = false;

    public hasOwnRates: boolean = false;

    public showCashTransactions: boolean = false;

    public showInterestAccruals: boolean = false;

    public vestingDate: Date = new Date();

    public async getAwardCosts(trancheId: number, vestingDate: Date, rate: number | null) {
        var criteria = new AwardCostsCriteria();
        criteria.trancheId = trancheId;

        this.vestingDate = vestingDate;

        const data = await this.taskRunner.waitForData(this.portfolioApiClient.getAwardCostsForTranche(criteria.toQueryObject()));
        this.awardCostsLookup?.set(data);

        this.showCashTransactions = this.awardCostsLookup.cashTransactions.length > 0;
        this.showInterestAccruals = this.awardCostsLookup.interestAccruals.length > 0;

        this.hasLinkedRates = this.awardCostsLookup.interestAccruals.some(c => c.linkedRate !== 1);
        this.hasOwnRates = this.awardCostsLookup.interestAccruals.some(c => c.ownRate !== 1);

        if (rate) {
            await this.setAwardCostsConvertedValue(this.awardCostsLookup, rate);
        }

        this.showAwardCostsModal = true;
    }

    private setAwardCostsConvertedValue(awardCostsLookup: AwardCostsLookup, rate: number) {
        awardCostsLookup.interestAccruals.forEach(interestAccrual => {
            interestAccrual.compoundedBalance *= rate;
            interestAccrual.openingAccrued *= rate;
            interestAccrual.accruedForPeriod *= rate;
            interestAccrual.closingAccrued *= rate;
            interestAccrual.accrualPaidAmount! *= rate;
        })

        awardCostsLookup.cashTransactions.forEach(cashTransaction => {
            cashTransaction.amount *= rate;
        })
    }
}

@observer
export class BalancesTrancheSummary extends React.Component<IBalancesSummaryProps>{

    private viewModel: BalancesTrancheSummaryVM = new BalancesTrancheSummaryVM();

    private formatUnits(value: number, item: any, shouldGroup: boolean) {
        const decimals = shouldGroup
            ? (item as CalculationGroup).details?.[0]?.trancheBalance?.instrument?.allowedDecimals ?? 0
            : (item as CalculationTranche).trancheBalance?.instrument?.allowedDecimals ?? 0;
        const format = "#,##0" + (decimals > 0 ? "." + "#".repeat(decimals) : "");
        return NumberUtils.format(value, format);
    }

    public render() {

        const group = this.props.group,
            applyPayoutRatio = this.props.applyPayoutRatio,
            awardName = group.awardName,
            shouldGroup = group.details.length > 5,
            tranches = this.getData(group.details, shouldGroup),
            currencySymbol = group.instrument.displayCurrencySymbol,
            noProfit = group.profitLossConvertedLimited === -1,
            groups = shouldGroup ? tranches as unknown as CalculationGroup[] : [],
            showReleaseDates = tranches.some(c => c.releaseDate.getTime() !== c.vestingDate.getTime()),
            hasCostsAlreadyPaidGroups = this.props.hasCostsAlreadyPaidGroups,
            showUnitConversionDetail = this.viewModel.awardCostsLookup.cashTransactions.some(cashTransaction => cashTransaction.isUnitConversion);

        const metricLabel = hasCostsAlreadyPaidGroups? "value" : "profit";

        return (
            <>
                <table className="tranches">
                    <tbody>
                        <tr>
                            <th> 
                                {group.trancheBalance.incentiveScheme.requiresBulkTrading ? "" : "Expiry date" }
                            </th>
                            <th style={{ minWidth: 200 }}></th>
                            <th></th>
                            <th>
                                {group.showUnitColumns ? "Traded units" : ""}
                            </th>
                            {tranches.map((t, index) =>
                                <th key={index} className={t.isVested ? "vested" : ""}>
                                    <span>
                                        {t.hasAcceleration &&
                                            <i className="fa fa-forward" data-tip={shouldGroup ? "Contains accelerated vestings" : "Vesting was accelerated"}></i>}
                                        {showReleaseDates ? (t.hasBeenReleased ? "Released" : "Releases On") : (!t.isVested ? "Vests" : "Vested")}<br />
                                        {DateUtils.format(showReleaseDates ? t.releaseDate : t.vestingDate, shouldGroup ? "yyyy" : "dd MMM yyyy")}
                                    </span>
                                </th>)}
                            <th className="vested">{showReleaseDates ? "Released" : "Vested"}<br />awards</th>
                        </tr>
                        {shouldGroup &&
                            <tr>
                                {!group.trancheBalance.incentiveScheme.requiresBulkTrading &&
                                    <td>{DateUtils.format(group.trancheBalance.effectiveExpiryDate, "dd MMM yyyy")}</td>
                                }
                                {group.trancheBalance.incentiveScheme.requiresBulkTrading &&
                                    <td></td>
                                }
                                <td className="text-heading">Number of vestings </td>
                                <td><button className='fa fa-info-circle info-btn '
                                    onClick={() => this.viewModel.showVestingSchedule = true}
                                /></td>
                                <td></td>
                                {groups.map((group: CalculationGroup, index) =>
                                    <td key={index} className={group.isVested ? "vested" : ""}>{group.details.length}</td>)}
                                <td>{tranches.sum(c => group.isVested ? group.details.length : 0)}</td>
                            </tr>
                        }
                        <tr>
                            {!shouldGroup && !group.trancheBalance.incentiveScheme.requiresBulkTrading &&
                                <td>{DateUtils.format(group.trancheBalance.effectiveExpiryDate, "dd MMM yyyy")}</td>}
                            {!shouldGroup && group.trancheBalance.incentiveScheme.requiresBulkTrading &&
                                <td></td>}
                            {shouldGroup &&
                                <td></td>}
                            {group.showUnitColumns && 
                                <>
                                <td className="text-heading bold">{applyPayoutRatio && group.payoutUnits !== group.availableBalance ? "Payout ratio units" : "Available units"}</td>
                                    <td></td>
                                    <td>{this.formatUnits(group.tradedUnits, group, true)}</td>
                                    {tranches.map((t, index) =>
                                        <td key={index} className={`${t.isVested ? "vested" : ""} ${(applyPayoutRatio && (shouldGroup
                                             ? ((t as unknown as CalculationGroup).payoutUnits !== (t as unknown as CalculationGroup).availableBalance
                                                 || (t as unknown as CalculationGroup).details.some(d => d.trancheBalance.hasUnprocessedAwardCondition))                                            : t.hasUnprocessedAwardCondition)) ? "payout-units" : ""}`}>
                                            {this.formatUnits(shouldGroup
                                                ? (applyPayoutRatio ? (t as unknown as CalculationGroup).payoutUnits : t.availableBalance)
                                                : (applyPayoutRatio && t.hasUnprocessedAwardCondition
                                                    ? (t as unknown as CalculationTranche).payoutUnits
                                                    : t.availableBalance), t, shouldGroup)}
                                        </td>)}
                                    <td className="vested">
                                        {this.formatUnits(applyPayoutRatio ? group.payoutVestedBalance : group.vestedBalance, group, true)}
                                    </td>
                                </>
                            }   
                        </tr>
                        <tr>
                            <td></td>
                            <td className="text-heading">Current gross value</td>
                            <td></td>
                            <td></td>
                            {tranches.map((t, index) =>
                                <td key={index} className={t.isVested ? "vested" : ""}>
                                    {this.format(shouldGroup
                                        ? (applyPayoutRatio ? (t as unknown as CalculationGroup).payoutCurrentValueConverted : t.currentValueConverted)
                                        : (applyPayoutRatio && t.hasUnprocessedAwardCondition
                                        ? t.currentValueConverted * t.payoutRatio
                                        : t.currentValueConverted), currencySymbol)}
                                </td>)}
                            <td>{this.format(applyPayoutRatio ? group.payoutVestedCurrentValueConverted : group.vestedCurrentValueConverted, currencySymbol)}
                            </td>
                        </tr>
                        {group.showAwardCostsColumn && 
                            <>
                                <tr>
                                    <td></td>
                                    
                                        <td className="text-heading">Award costs/income</td>
                                    <td></td>
                                    <td></td>
                                    {tranches.map((t, index) =>
                                        <td key={index} className={t.isVested ? "vested" : ""}>
                                            {!shouldGroup && (!applyPayoutRatio || !t.hasUnprocessedAwardCondition) &&
                                                <Neo.Tooltip
                                                content={'Click to view the award cost breakdown'} position="right" className="background-secondary" inline>
                                                    <button className='fa fa-info-circle info-btn '
                                                        onClick={() => this.viewModel.getAwardCosts(t.trancheId, t.vestingDate, group.instrument.rate)}
                                                        />
                                                </Neo.Tooltip>
                                            }
                                            {this.format(shouldGroup
                                                ? (applyPayoutRatio ? (t as unknown as CalculationGroup).payoutRemainingAwardDebtConverted : t.remainingAwardDebtConverted)
                                                : (applyPayoutRatio && t.hasUnprocessedAwardCondition
                                                ? t.remainingAwardDebtConverted * t.payoutRatio
                                                : t.remainingAwardDebtConverted), currencySymbol)}
                                        </td>)}
                                    <td>{this.format(applyPayoutRatio ? group.payoutVestedAwardDebtConverted : group.vestedAwardDebtConverted, currencySymbol)}
                                    </td>
                                </tr>
                            </>
                        }
                        <tr>
                            <td></td>
                            <td className="text-heading bold">Total potential {metricLabel}</td>
                            <td></td>
                            <td></td>
                            {tranches.map((t, index) =>
                                <td key={index} className={t.isVested ? "vested" : ""}>
                                    {this.format(noProfit ? 0 : (shouldGroup
                                        ? (applyPayoutRatio ? (t as unknown as CalculationGroup).payoutProfitLossConverted : t.profitLossConverted)
                                        : (applyPayoutRatio && t.hasUnprocessedAwardCondition
                                        ? t.profitLossConverted * t.payoutRatio
                                        : t.profitLossConverted)), currencySymbol)}
                                </td>)}
                            <td className="vested bold">{this.format(applyPayoutRatio ? group.payoutVestedProfitLossConvertedLimited : group.vestedProfitLossConvertedLimited, currencySymbol)}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <Neo.Modal title="Vesting Schedule" size='xl'
                    bind={this.viewModel.meta.showVestingSchedule}>
                    <NeoGrid.Grid items={groups} className="tranches">
                        {(item: CalculationGroup, meta) => (
                            <NeoGrid.RowGroup expandProperty={meta?.isExpanded}>
                                <NeoGrid.Row >
                                    <NeoGrid.Column className={item.isVested ? "vested bold" : ""} display={meta?.vestingDate} dateProps={{ formatString: "yyyy" }} />
                                </NeoGrid.Row>
                                <NeoGrid.ChildRow >
                                    <NeoGrid.Grid items={item.details.sortBy(c => c.vestingDate)}>
                                        {(childItem, childMeta) => (
                                            <NeoGrid.RowGroup>
                                                <NeoGrid.Row>
                                                    <NeoGrid.Column display={childMeta?.vestingDate} dateProps={{ formatString: "dd MMMM yyyy" }}
                                                        label={item.isVested ? "Date Vested" : "Vesting Date"} className={item.isVested ? "text-end vested" : "text-end"} />
                                                    <NeoGrid.Column display={childMeta?.availableBalance} label={"Available Balance"}
                                                        className={item.isVested ? "text-end vested" : "text-end"} />
                                                    {group.showAwardCostsColumn && 
                                                        <>
                                                            <NeoGrid.Column display={childMeta?.remainingAwardDebt} label={"Award Costs"}
                                                                className={item.isVested ? "text-end vested" : "text-end"}
                                                                numProps={{ currencySymbol: currencySymbol, formatString: "#,##0.00##" }}>
                                                            </NeoGrid.Column>
                                                            <NeoGrid.Column>
                                                                <Neo.Tooltip
                                                                    content={'Click to view the award cost breakdown'} position="right" className="background-secondary" inline>
                                                                    <button className='fa fa-info-circle info-btn '
                                                                        onClick={() => this.viewModel.getAwardCosts(childItem.trancheId, childItem.vestingDate, group.instrument.rate)}
                                                                    />
                                                                </Neo.Tooltip>
                                                            </NeoGrid.Column>
                                                        </>
                                                    }
                                                </NeoGrid.Row>
                                            </NeoGrid.RowGroup>
                                        )}
                                    </NeoGrid.Grid>
                                </NeoGrid.ChildRow>
                            </NeoGrid.RowGroup>
                        )}
                    </NeoGrid.Grid>
                </Neo.Modal>

                <Neo.Modal title={`Award Costs for ${awardName} | Vesting: ${DateUtils.format(this.viewModel.vestingDate, "dd MMM yy")} `} bind={this.viewModel.meta.showAwardCostsModal} size={"xl"}>
                    <div>
                        {this.viewModel.showCashTransactions &&
                            <Neo.Card title={"Cash Transactions"} icon="money-bill">
                                <NeoGrid.Grid items={this.viewModel.awardCostsLookup.cashTransactions}>
                                    {(cashTransaction, cashTransactionMeta) => (
                                        <NeoGrid.Row>
                                            <NeoGrid.Column display={cashTransactionMeta.effectiveDate} label={"Date"} />
                                            <NeoGrid.Column display={cashTransactionMeta.transactionType} label={"Type"} />
                                            <NeoGrid.Column display={cashTransactionMeta.detail} label={"Detail"} hidden={!showUnitConversionDetail}/>
                                            <NeoGrid.Column display={cashTransactionMeta.amount} label={`Amount (${currencySymbol})`} numProps={{ currencySymbol: currencySymbol }} sum />
                                        </NeoGrid.Row>
                                    )}
                                </NeoGrid.Grid>
                            </Neo.Card>
                        }

                        {this.viewModel.showInterestAccruals &&
                            <Neo.Card title={"Interest Accruals"} icon="chart-line">
                                <NeoGrid.Grid items={this.viewModel.awardCostsLookup.interestAccruals} allowSort={false}>
                                    {(interestAccrual, interestAccrualMeta) => (
                                        <NeoGrid.Row>
                                            <NeoGrid.Column display={interestAccrualMeta.startDate} />
                                            <NeoGrid.Column display={interestAccrualMeta.endDate} label={"End Date\n(excl.)"} />
                                            <NeoGrid.Column display={interestAccrualMeta.noOfDays} />
                                            {this.viewModel.hasLinkedRates &&
                                                <NeoGrid.Column display={interestAccrualMeta.linkedRate} numProps={{ formatString: "#,##0.00##%" }} />}
                                            {this.viewModel.hasOwnRates &&
                                                <NeoGrid.Column display={interestAccrualMeta.ownRate} numProps={{ formatString: "#,##0.00##%" }} />}
                                            <NeoGrid.Column display={interestAccrualMeta.compoundedBalance} label={`Capital (${currencySymbol})`} />
                                            <NeoGrid.Column display={interestAccrualMeta.openingAccrued} label={`Opening accrued (${currencySymbol})`} />
                                            <NeoGrid.Column display={interestAccrualMeta.accrualPaidAmount} label={`Accrued paid amount (${currencySymbol})`} />
                                            <NeoGrid.Column display={interestAccrualMeta.accruedForPeriod} label={`Accrued for period (${currencySymbol})`} />
                                            <NeoGrid.Column display={interestAccrualMeta.closingAccrued} label={`Closing accrued (${currencySymbol})`} />
                                        </NeoGrid.Row>
                                    )}
                                </NeoGrid.Grid>
                            </Neo.Card>
                        }
                    </div>
                </Neo.Modal>
            </>
        )
    }

    private getData(tranches: CalculationTranche[], shouldGroup: boolean): ITrancheDetails[] {

        if (shouldGroup) {
            var trancheGrouping = tranches.groupBy(c => c.vestingDate.getFullYear() + (c.isVested ? "vested" : "unvested")).map(g => {
                const group = new CalculationGroup(g.item.trancheBalance);
                group.details = g.details;
                return group;
            })
            return trancheGrouping;
        } else {
            return tranches;
        }
    }

    private format(value: number, currencySymbol: string) {
        return NumberUtils.format(value, Misc.NumberFormat.CurrencyDecimals, currencySymbol);
    }
}